import type { ListBuilderConfiguration, ListBuilderList } from '../types';

/**
 * PD-16211 — Replenishment (Distribution → Replenishment) as a list.
 *
 * The screen was a bespoke table that loaded every customer at once and
 * ranked them by the number of units suggested. It is a list-builder list
 * now, so filtering, saved searches, column configuration and paging come
 * from the same place as every other list, and the rows arrive one page at
 * a time ordered by need.
 *
 * Like the Registry list, the rows come from a service rather than a
 * table: `objectTable: 'replenishment'` is only a naming scope for the
 * field and filter keys, corresponds to no entity, and is never resolved
 * through `applyFiltersToQueryBuilder`. `ReplenishmentListService` reads
 * the filter keys itself and builds the SQL, including the need score the
 * default sort uses.
 *
 * The quantity boxes are NOT here. One column per replenish product would
 * have to be written out ahead of time, and the products are tenant data,
 * so the module injects those columns at runtime once it knows what the
 * tenant replenishes (see `registerReplenishmentColumns`). Everything a
 * column picker can meaningfully offer is below.
 */
const defaultReplenishmentList: ListBuilderList = {
	id: 'default-replenishment',
	name: 'Needs Product',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {},
	columns: [
		{
			id: 'replenishment-customer',
			fields: [
				{
					id: 'replenishment-customer-name',
					dataConfig: {
						type: 'field',
						field: { fieldName: 'name', objectTable: 'replenishment' },
						defaultValue: '-',
					},
					link: {
						enabled: true,
						type: 'object',
						objectTable: 'client',
						idField: 'clientId',
					},
					styleConfig: {
						size: 'md',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'replenishment-customer-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'replenishment-customer-address',
					dataConfig: {
						type: 'field',
						field: { fieldName: 'addressLabel', objectTable: 'replenishment' },
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
			],
			label: 'Customer',
			headers: [{ id: 'h-replenishment-customer', text: 'Customer' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'replenishment' },
			],
			minWidth: 240,
			widthConfig: 'large',
		},
		{
			id: 'replenishment-due',
			fields: [
				{
					id: 'replenishment-due-date',
					dataConfig: {
						type: 'field',
						field: { fieldName: 'dueDate', objectTable: 'replenishment' },
						defaultValue: '-',
					},
					styleConfig: {
						size: 'md',
						// Static turns on the renderer's own due coloring: past
						// due red, today amber, this week blue.
						colorRule: { type: 'static' },
						// Days always: "12 days ago" says more to a dispatcher
						// than "2 weeks ago".
						formatOptions: { relativeBase: 'today', relativeUnit: 'day' },
					},
					displayStyle: 'relative_date',
				},
			],
			label: 'Due',
			headers: [{ id: 'h-replenishment-due', text: 'Due' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{ direction: 'ASC', fieldName: 'due', objectTable: 'replenishment' },
			],
			minWidth: 110,
			widthConfig: 'small',
		},
		{
			id: 'replenishment-frequency',
			fields: [
				{
					id: 'replenishment-frequency-days',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'intervalDays',
							objectTable: 'replenishment',
						},
						defaultValue: '-',
					},
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'text',
					suffix: ' days',
				},
			],
			label: 'Frequency',
			headers: [{ id: 'h-replenishment-frequency', text: 'Frequency' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			// Shown as text ("7 days") but sorts as a number.
			sortValueType: 'number',
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'frequency',
					objectTable: 'replenishment',
				},
			],
			minWidth: 100,
			widthConfig: 'small',
		},
		{
			id: 'replenishment-fill',
			fields: [
				{
					id: 'replenishment-fill-pct',
					dataConfig: {
						type: 'field',
						field: { fieldName: 'fillPct', objectTable: 'replenishment' },
						defaultValue: '-',
					},
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
						// Percent display forces its own format; auto is how the
						// renderer is told to trim the trailing zeros.
						formatOptions: { numberFormat: 'auto', decimalPlaces: 0 },
					},
					displayStyle: 'percent',
				},
			],
			label: 'Fill',
			headers: [{ id: 'h-replenishment-fill', text: 'Fill' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [
				{ direction: 'ASC', fieldName: 'fill', objectTable: 'replenishment' },
			],
			minWidth: 90,
			widthConfig: 'small',
		},
	],
	// PD-16211 — the need score orders the list but is not shown: it is a
	// ranking, not a figure anyone acts on, and what it is made of has its own
	// columns (Fill, Due, Frequency). So it is a sort without a column.
	defaultSortId: 'replenishment-need-desc',
	actions: [],
};

export const REPLENISHMENT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [defaultReplenishmentList],
	tableSorts: [
		{
			id: 'replenishment-need-desc',
			name: 'Need (High to Low)',
			description: 'Customers closest to running out first',
			fields: [
				{ direction: 'DESC', fieldName: 'need', objectTable: 'replenishment' },
			],
		},
		{
			id: 'replenishment-need-asc',
			name: 'Need (Low to High)',
			fields: [
				{ direction: 'ASC', fieldName: 'need', objectTable: 'replenishment' },
			],
		},
	],
	showResetButton: true,
	filters: {
		activeFilters: [
			'replenishmentRoute',
			'replenishmentBelowThreshold',
			'replenishmentSensorDown',
		],
	},
	objects: [
		{
			baseUrl: '/distribution/replenishment',
			objectLabel: 'Replenishment',
			objectTable: 'replenishment',
			selectedFields: [
				{ fieldName: 'name', fieldLabel: 'Customer' },
				{ fieldName: 'addressLabel', fieldLabel: 'Address' },
				{ fieldName: 'dueDate', fieldLabel: 'Due' },
				{ fieldName: 'intervalDays', fieldLabel: 'Frequency (days)' },
				{ fieldName: 'fillPct', fieldLabel: 'Fill %' },
				{ fieldName: 'totalDemand', fieldLabel: 'Units suggested' },
			],
		},
		// So the customer name links to /clients/:id rather than the
		// fallback /client/<id>, which the router does not serve.
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
